import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DokobitSignModal } from "@features/DokobitSignModal/DokobitSignModal";
import { PowerOfAttorneyBudgetToolsOfferModal } from "@features/PowerOfAttorneyBudgetToolsOfferModal/PowerOfAttorneyBudgetToolsOfferModal";
import { Button } from "@components/Button";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useScript } from "@hooks/useScript";
import { useModalActions, type IUseModalProps } from "@store/modal/modal";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { type IOverview } from "@services/overview/types";
import { useSubmitPOADokobitConsentMutation } from "@services/powersOfAttorney/mutations";
import { usePOAStatementConsentsStatusQuery } from "@services/powersOfAttorney/queries";
import { JQUERY_SCRIPT_URL } from "@/constants";
import { usePowerOfAttorneyDokobitConsentSchema } from "./usePowerOfAttorneyDokobitConsentSchema";

interface IFormData {
  firstName: string;
  lastName: string;
  personalCode: string;
}

interface IPowerOfAttorneyDokobitConsentModalProps extends IUseModalProps {
  overviewData: IOverview;
  modalStack: number[];
  powerOfAttorneyLedgerConsentId?: string;
}

export const PowerOfAttorneyDokobitConsentModal: React.FC<
  IPowerOfAttorneyDokobitConsentModalProps
> = (props) => {
  const { overviewData, modalStack, powerOfAttorneyLedgerConsentId, ...rest } =
    props;
  const { data: customerDetails } = useCustomerDetailsQuery();
  const modal = useModal(rest);
  const { create } = useModalActions();
  const { t } = useTranslation();
  // Preload for Dokobit sign modal
  useScript(JQUERY_SCRIPT_URL);

  const schema = usePowerOfAttorneyDokobitConsentSchema();

  const { data: consentStatus } = usePOAStatementConsentsStatusQuery(
    {
      powerOfAttorneyLedgerConsentId: powerOfAttorneyLedgerConsentId ?? "",
    },
    Boolean(powerOfAttorneyLedgerConsentId),
  );

  const { handleSubmit, control } = useForm<IFormData>({
    defaultValues: {
      firstName: overviewData.firstName,
      lastName: overviewData.lastName,
      personalCode: "",
    },
    resolver: zodResolver(schema),
  });

  const { mutateAsync: submitPOADokobitConsent, isPending } =
    useSubmitPOADokobitConsentMutation();

  const { toastApiError } = useApiErrorUtils();

  const onSubmit: SubmitHandler<IFormData> = async (data) => {
    const responseData = await submitPOADokobitConsent({
      ...data,
      rulesAccepted: true,
    }).catch(toastApiError);

    if (!responseData) {
      return;
    }

    create((modalProps) => (
      <DokobitSignModal
        dokobitData={responseData}
        {...modalProps}
        important
        modalStack={[...modalStack, modal.id]}
        onSuccess={() => {
          if (consentStatus?.succeeded) {
            create((modalProps) => (
              <PowerOfAttorneyBudgetToolsOfferModal
                {...modalProps}
                ledgerConsentId={consentStatus.ledgerConsentId}
              />
            ));
          }
        }}
      />
    ));
  };

  return (
    <Modal
      modal={modal}
      title={t("powerOfAttorney.dokobitConsentModal.title")}
      className="md:max-w-3xl"
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("powerOfAttorney.dokobitConsentModal.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("powerOfAttorney.dokobitConsentModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 text-sm text-grey-dark">
          <span>
            {t("powerOfAttorney.dokobitConsentModal.consentText1", {
              email: customerDetails?.billForwardEmail,
            })}
          </span>
          <span>{t("powerOfAttorney.dokobitConsentModal.consentText2")}</span>
        </div>
        <div className="flex flex-col gap-4">
          <div className="contents gap-4 sm:flex">
            <FormInput
              name="firstName"
              control={control}
              label={t(
                "powerOfAttorney.dokobitConsentModal.form.fields.firstName.label",
              )}
            />
            <FormInput
              name="lastName"
              control={control}
              label={t(
                "powerOfAttorney.dokobitConsentModal.form.fields.lastName.label",
              )}
            />
          </div>
          <FormInput
            name="personalCode"
            control={control}
            label={t(
              "powerOfAttorney.dokobitConsentModal.form.fields.personalCode.label",
            )}
          />
        </div>
        <span className="text-sm text-grey-dark">
          {t("powerOfAttorney.dokobitConsentModal.footer")}
        </span>
      </div>
    </Modal>
  );
};
